import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { Spinner } from '../../components/ui/Spinner';
import type { FilaCategoria, MesInforme } from '../../../shared/facturacion-informe';

/**
 * Pestaña «Facturación» del análisis (CEO, 27/10/2026): lo que APPROX ha
 * facturado al cliente según el ERP, mes a mes y por categoría, con el
 * descuento técnico, los abonos, el rappel y la comisión de cada mes. El año
 * anterior se compara a fecha equivalente. Datos: GET /api/analisis/facturacion.
 */

type Respuesta = {
  cliente: { codigo: string; nombre: string };
  anios: number[];
  anio: number;
  nivel: 'categoria' | 'subcategoria';
  datos_hasta: string | null;
  meses: MesInforme[];
  categorias: FilaCategoria[];
  totales: {
    bruto: number; dto_tecnico: number; neto: number; neto_py: number; delta: number | null;
    abonos: number; comision: number; pagos_rappel: number; meses_con_datos: number;
  };
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const eur0 = (v: number) => (v === 0 ? '—' : new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(v));
const delta = (v: number | null) => (v === null ? '—' : `${v > 0 ? '+' : ''}${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })} %`);

export function FacturacionCliente({ cliente, base }: { cliente: string; base: string }) {
  const [anio, setAnio] = useState<number | null>(null);
  const [nivel, setNivel] = useState<'categoria' | 'subcategoria'>('categoria');
  const qs = new URLSearchParams({ cliente, nivel });
  if (anio) qs.set('anio', String(anio));
  const q = useQuery({
    queryKey: ['analisis-facturacion', qs.toString()],
    queryFn: () => api.get<Respuesta>(`/analisis/facturacion?${qs.toString()}`),
    staleTime: 60_000,
  });
  const d = q.data;

  if (q.isLoading) return <div className="py-10"><Spinner /></div>;
  if (q.error) return <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudo cargar la facturación. {(q.error as Error).message}</p>;
  if (!d) return null;
  if (!d.anios.length) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
        {cliente} no tiene facturación del ERP cargada. Se carga en <Link className="underline" to={`${base}/importador-ficheros?tab=facturacion`}>Importador de ficheros → Resumen de facturación mensual</Link>.
      </p>
    );
  }
  const t = d.totales;
  const hayComision = d.meses.some((m) => m.comision !== null);
  const hayRappel = d.meses.some((m) => m.pagos_rappel);
  const sinEnlazar = d.categorias.find((c) => c.sin_enlazar);

  const exportar = () => {
    const sep = ';';
    const num = (v: number) => v.toFixed(2).replace('.', ',');
    const cab = [d.nivel === 'subcategoria' ? 'Categoría;Subcategoría' : 'Categoría', ...MESES.map((m) => `${m} ${d.anio}`), `Total ${d.anio}`, `${d.anio - 1} (mismos meses)`, 'Variación %', 'Peso %'];
    const filas = d.categorias.map((c) => [
      d.nivel === 'subcategoria' ? `${c.categoria}${sep}${c.subcategoria ?? ''}` : c.categoria,
      ...c.meses.map(num), num(c.total), num(c.total_py), c.delta === null ? '' : String(c.delta).replace('.', ','), String(c.peso).replace('.', ','),
    ].join(sep));
    const blob = new Blob([`${String.fromCharCode(0xfeff)}${[cab.join(sep), ...filas].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `facturacion-${cliente.toLowerCase()}-${d.anio}-por-${d.nivel}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <Grupo label="Año">
          {d.anios.map((a) => <Pildora key={a} activo={d.anio === a} onClick={() => setAnio(a)}>{a}</Pildora>)}
        </Grupo>
        <Grupo label="Detalle">
          <Pildora activo={nivel === 'categoria'} onClick={() => setNivel('categoria')}>Categoría</Pildora>
          <Pildora activo={nivel === 'subcategoria'} onClick={() => setNivel('subcategoria')}>Subcategoría</Pildora>
        </Grupo>
        <span className="ml-auto text-xs text-slate-500">Facturación del ERP de APPROX hasta {fmt.date(d.datos_hasta)}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi titulo={`Facturado ${d.anio}`} valor={fmt.eur(t.neto)} nota={`tras dto. técnico · ${t.meses_con_datos} ${t.meses_con_datos === 1 ? 'mes' : 'meses'} con datos`} fuerte />
        <Kpi titulo={`Frente a ${d.anio - 1}`} valor={delta(t.delta)} nota={t.neto_py ? `${fmt.eur(t.neto_py)} en los mismos meses` : `sin facturación en ${d.anio - 1}`} />
        <Kpi titulo="Descuento técnico" valor={fmt.eur(t.dto_tecnico)} nota={`sobre ${fmt.eur(t.bruto)} brutos`} />
        <Kpi titulo={hayComision ? 'Comisión' : 'Abonos'} valor={fmt.eur(hayComision ? t.comision : t.abonos)} nota={hayComision ? 'la que dice el informe de cada mes' : 'ya restados del facturado'} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <caption className="px-4 py-2 text-left text-sm font-semibold text-slate-800">Mes a mes</caption>
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Mes</th>
              <th className="px-4 py-2 text-right">Bruto</th>
              <th className="px-4 py-2 text-right">Dto. técnico</th>
              <th className="px-4 py-2 text-right">Facturado</th>
              <th className="px-4 py-2 text-right">Abonos</th>
              <th className="px-4 py-2 text-right">{d.anio - 1}</th>
              {hayRappel && <th className="px-4 py-2 text-right" title="Líneas de pago de rappel (código 999999) facturadas en el mes">Pagos de rappel</th>}
              {hayComision && <th className="px-4 py-2 text-right">Comisión</th>}
              <th className="px-4 py-2 text-right" title="Neto del informe del ERP del mes frente a lo importado">Cuadre con el informe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.meses.map((m, i) => {
              const dif = m.neto_informe === null ? null : Math.round((m.neto_informe - m.neto) * 100) / 100;
              return (
                <tr key={m.mes} className={m.con_datos ? '' : 'text-slate-400'}>
                  <td className="px-4 py-1.5 font-medium">{MESES[i]} {d.anio}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{m.con_datos ? fmt.eur(m.bruto) : '—'}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-500">{m.dto_tecnico ? `−${fmt.eur(m.dto_tecnico)}` : '—'}</td>
                  <td className="px-4 py-1.5 text-right font-semibold tabular-nums">{m.con_datos ? fmt.eur(m.neto) : '—'}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-500">{m.abonos ? fmt.eur(m.abonos) : '—'}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-500">{m.neto_py ? fmt.eur(m.neto_py) : '—'}</td>
                  {hayRappel && <td className="px-4 py-1.5 text-right tabular-nums">{m.pagos_rappel ? fmt.eur(m.pagos_rappel) : '—'}</td>}
                  {hayComision && <td className="px-4 py-1.5 text-right tabular-nums">{m.comision !== null ? fmt.eur(m.comision) : '—'}</td>}
                  <td className={`px-4 py-1.5 text-right tabular-nums ${dif === null ? 'text-slate-400' : Math.abs(dif) >= 1 ? 'font-semibold text-warn-700' : 'text-ok-700'}`}
                    title={dif === null ? 'Sin informe del ERP de este mes' : `Informe ${fmt.eur(m.neto_informe)} · importado ${fmt.eur(m.neto)}`}>
                    {dif === null ? 'sin informe' : Math.abs(dif) < 1 ? 'cuadra' : `${dif > 0 ? '+' : ''}${fmt.eur(dif)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
            <tr>
              <td className="px-4 py-2">Total {d.anio}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(t.bruto)}</td>
              <td className="px-4 py-2 text-right tabular-nums text-slate-500">{t.dto_tecnico ? `−${fmt.eur(t.dto_tecnico)}` : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(t.neto)}</td>
              <td className="px-4 py-2 text-right tabular-nums text-slate-500">{t.abonos ? fmt.eur(t.abonos) : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums text-slate-500">{fmt.eur(d.meses.reduce((s, m) => s + m.neto_py, 0))}</td>
              {hayRappel && <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(t.pagos_rappel)}</td>}
              {hayComision && <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(t.comision)}</td>}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
          <h2 className="text-sm font-semibold text-slate-800">Por {d.nivel === 'subcategoria' ? 'subcategoría' : 'categoría'} (facturado tras dto. técnico)</h2>
          <button type="button" onClick={exportar} className="cursor-pointer rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Descargar CSV</button>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">{d.nivel === 'subcategoria' ? 'Categoría › subcategoría' : 'Categoría'}</th>
              {MESES.map((m) => <th key={m} className="px-2 py-2 text-right">{m}</th>)}
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right" title="Mismos meses del año anterior">{d.anio - 1}</th>
              <th className="px-3 py-2 text-right">Var.</th>
              <th className="px-3 py-2 text-right">Peso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.categorias.map((c) => (
              <tr key={`${c.categoria}|${c.subcategoria ?? ''}`} className={c.sin_enlazar ? 'bg-warn-50' : ''}>
                <td className="whitespace-nowrap px-3 py-1.5 font-medium text-slate-800">
                  {c.categoria}{c.subcategoria ? <span className="text-slate-500"> › {c.subcategoria}</span> : null}
                </td>
                {c.meses.map((v, i) => <td key={i} className="px-2 py-1.5 text-right tabular-nums">{eur0(v)}</td>)}
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{fmt.eur(c.total)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{c.total_py ? fmt.eur(c.total_py) : '—'}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${c.delta === null ? 'text-slate-400' : c.delta >= 0 ? 'text-ok-700' : 'text-bad-700'}`}>{delta(c.delta)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{c.peso.toLocaleString('es-ES')} %</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
            <tr>
              <td className="px-3 py-2">Total</td>
              {d.meses.map((m) => <td key={m.mes} className="px-2 py-2 text-right tabular-nums">{eur0(m.neto)}</td>)}
              <td className="px-3 py-2 text-right tabular-nums">{fmt.eur(t.neto)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmt.eur(t.neto_py)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{delta(t.delta)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
        {sinEnlazar && (
          <p className="border-t border-slate-100 px-4 py-2 text-xs text-warn-700">
            «Sin enlazar» son ventas de códigos del ERP que no tienen producto en la app ({fmt.eur(sinEnlazar.total)} en {d.anio}). Se asignan en <Link className="underline" to={`${base}/importaciones`}>Ficheros importados → Pendientes de enlazar</Link> y pasan a su categoría.
          </p>
        )}
      </div>
    </div>
  );
}

function Kpi({ titulo, valor, nota, fuerte }: { titulo: string; valor: string; nota: string; fuerte?: boolean }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{titulo}</div>
      <div className={`mt-1 font-display tabular-nums ${fuerte ? 'text-2xl font-extrabold text-slate-900' : 'text-xl font-bold text-slate-800'}`}>{valor}</div>
      <div className="mt-0.5 text-xs text-slate-500">{nota}</div>
    </div>
  );
}

function Grupo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </div>
  );
}

function Pildora({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activo}
      className={`cursor-pointer rounded-md border px-2.5 py-1 text-xs font-semibold transition ${activo ? 'border-accent-600 bg-accent-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-accent-300 hover:text-slate-900'}`}>
      {children}
    </button>
  );
}
